import { useEffect, useState, useRef } from 'react';
import { X, MagnifyingGlass } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { mfgApi } from '@/pages/mfg/mfgApi';

// ── WorkOrderPicker ─────────────────────────────────────────────────────────
export function WorkOrderPicker({ value, onChange, valueLabel, testId = 'pr-mfg-order' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const h = setTimeout(() => {
      mfgApi.workOrders({ q: q || undefined, status: 'all' })
        .then((all) => {
          // Keep only active WOs to avoid linking to completed/cancelled jobs
          const active = (all || []).filter((w) => ['planned', 'released', 'in_progress'].includes(w.status));
          setRows(active.slice(0, 40));
        })
        .catch((err) => { console.error('[pr] wo search failed', err); setRows([]); })
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(h);
  }, [open, q]);

  // Click-away close
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className="h-10 mt-1 w-full flex items-center justify-between px-3 rounded-md border border-zinc-200 bg-white text-left text-sm hover:border-zinc-300 transition-colors">
        <span className={value ? 'text-zinc-800 font-medium truncate' : 'text-zinc-400'}>
          {value ? (valueLabel || value) : '作業指示を選択...'}
        </span>
        <span className="flex items-center gap-1 shrink-0 ml-2">
          {value && (
            <span data-testid={`${testId}-clear`}
              onClick={(e) => { e.stopPropagation(); onChange('', ''); }}
              className="text-zinc-300 hover:text-rose-500 rounded px-1">
              <X size={14} />
            </span>
          )}
          <span className="text-zinc-400"><MagnifyingGlass size={14} /></span>
        </span>
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full bg-white rounded-lg border border-zinc-200 shadow-lg overflow-hidden" data-testid={`${testId}-panel`}>
          <div className="p-2 border-b border-zinc-200">
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="製番・品名・作業No で検索"
              className="h-9 text-sm" data-testid={`${testId}-search`} />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading && <p className="px-3 py-4 text-center text-xs text-zinc-400">読み込み中...</p>}
            {!loading && rows.length === 0 && <p className="px-3 py-4 text-center text-xs text-zinc-400">該当する作業指示がありません</p>}
            {!loading && rows.map((w) => (
              <button key={w.id} type="button" data-testid={`${testId}-opt-${w.id}`}
                onClick={() => { onChange(w.id, w.manufacturing_no || w.wo_no); setOpen(false); }}
                className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center gap-2 text-sm">
                <span className="font-mono text-[11px] text-zinc-500 shrink-0">{w.manufacturing_no || w.wo_no}</span>
                <span className="text-zinc-800 truncate flex-1">{w.product_name || '—'}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                  w.status === 'in_progress' ? 'bg-emerald-50 text-emerald-700' :
                  w.status === 'released'    ? 'bg-blue-50 text-blue-700'      :
                  'bg-zinc-100 text-zinc-500'
                }`}>{w.status_label || w.status}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
